import { FC, useState } from 'react';
import Grid from '@material-ui/core/Grid';
import { Controller, SubmitHandler, useFormContext } from 'react-hook-form';
import { ControllerRenderProps } from 'react-hook-form/dist/types/controller';
import { FieldValues } from 'react-hook-form/dist/types';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';
import safeJsonStringify from 'safe-json-stringify';

import useModal from '@yaydoo/react-components/lib/hooks/useModal';
import useToast from '@yaydoo/react-components/lib/hooks/useToast';

import SmartField from '@components/SmartField';
import useSmartFields from '@hooks/useSmartFields';
import useStore from '@hooks/useStore';
import { ISmartField, ISmartFieldCustomEventDetail } from '@components/SmartField/types';
import Input from '@components/common/Input';
import PayButton from '@components/PayButton';
import Redirect3dSecure from '@components/modals/Redirect3dSecure';
import { IDLocalPaymentData, PartnerFormProps } from '@containers/checkout/payment-form/types';
import { appUrl } from '@configs/variables';
import FormErrorMessage from '@components/FormErrorMesage';
import { Redirect3dSecureError } from '@store/dlocal';
import { appendQueryParams } from '@utils/appendQueryParams';
import getUrlParam from '@utils/getUrlParam';
import { Partner, PaymentLinkRedirectParams } from '@constants/paymentLink';
import sendPaymentError from '@utils/sendPaymentError';
import { PaymentMethod } from '@store/checkout';
import { IGetCardInfo } from '@utils/getCardInfo';

const DLocalCardForm: FC<PartnerFormProps> = ({ onValidationError = () => null, apiKey }) => {
  const { t } = useTranslation();
  const { showModal } = useModal();
  const { openToast } = useToast();
  const { fields, dlocal: dlocalClient, deviceId } = useSmartFields({ apiKey });
  const [cardField, setCardField] = useState<ISmartField | null>(null);
  const [paymentError, setPaymentError] = useState(false);
  const { control, formState, handleSubmit } = useFormContext();
  const {
    dlocal,
    checkout,
    links: {
      paymentLink: { paymentLinkId, total: amount },
    },
  } = useStore();

  const cardHandler = (
    event: CustomEvent<ISmartFieldCustomEventDetail>,
    field: ControllerRenderProps<FieldValues, 'cardNumber'>,
  ) => {
    setCardField(event.detail.field);
    // To set value in to React hook forms
    field.onChange(event.detail.data.complete);
  };
  const expirationHandler = (
    event: CustomEvent<ISmartFieldCustomEventDetail>,
    field: ControllerRenderProps<FieldValues, 'cardExpiration'>,
  ) => {
    // To set value in to React hook forms
    field.onChange(event.detail.data.complete);
  };
  const cvvHandler = (
    event: CustomEvent<ISmartFieldCustomEventDetail>,
    field: ControllerRenderProps<FieldValues, 'cardCvv'>,
  ) => {
    // To set value in to React hook forms
    field.onChange(event.detail.data.complete);
  };

  const getCardInfo = async (): Promise<IGetCardInfo | undefined> => {
    if (!cardField) return;

    const card = await dlocalClient.getBinInformation(cardField);

    return {
      bin: String(card.bin),
    };
  };

  const getToken = async (data: IDLocalPaymentData) => {
    if (!cardField) return;

    try {
      const { token } = await dlocalClient.createToken(cardField, {
        name: data.cardHolderName,
      });
      return token;
    } catch (e) {
      const error = e as Record<string, unknown>;
      const card = await getCardInfo();
      const cardInfo = {
        holderName: data.cardHolderName,
        bin: String(card?.bin),
      };

      sendPaymentError({
        ...data,
        partner: Partner.DLOCAL,
        paymentMethod: PaymentMethod.card,
        error: safeJsonStringify(error),
        cardInfo,
      });
    }
  };

  const startPaymentTransaction = async (data: IDLocalPaymentData) => {
    if (!cardField) return;

    const card = await getCardInfo();
    const cardInfo = {
      holderName: data.cardHolderName,
      bin: String(card?.bin),
    };

    const result = await dlocal.initCardPaymentTransaction(data, cardInfo);
    if (!result) return;
    const { metadata } = result;
    const { redirectUrl } = metadata;
    return showModal({
      component: Redirect3dSecure,
      props: { redirectUrl, amount },
    });
  };

  const pay = async (values: IDLocalPaymentData) => {
    try {
      setPaymentError(false);
      const redirect_uri = getUrlParam(PaymentLinkRedirectParams.REDIRECT_URL);
      const confirmationUrl = appendQueryParams(
        `${appUrl}/pay/${paymentLinkId}`,
        { redirect_uri },
        { clearEmpty: true },
      );
      const data: IDLocalPaymentData = {
        ...values,
        paymentLinkId,
        deviceId,
        confirmationUrl,
      };
      const token = await getToken(data);
      if (token) await startPaymentTransaction({ ...data, token });
    } catch (e) {
      setPaymentError(true);
      if (e instanceof Redirect3dSecureError) {
        openToast({
          type: 'error',
          title: t('checkoutScreen.payment.errors.card.title'),
          message: t('checkoutScreen.payment.errors.card.redirect3ds'),
        });
      }
    }
  };

  return (
    <Grid container spacing={2}>
      <FormErrorMessage
        visible={paymentError}
        message={t('checkoutScreen.payment.errors.card.unknown')}
      />
      <Grid item xs={12}>
        <Controller
          name="cardHolderName"
          control={control}
          defaultValue=""
          rules={{ required: true }}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              data-testid="cardholder-name"
              fullWidth
              autoComplete="off"
              label={t('checkoutScreen.payment.methods.card.name')}
              disabled={formState.isSubmitting}
              error={fieldState.invalid || paymentError}
            />
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <Controller
          name="cardNumber"
          control={control}
          defaultValue={false}
          rules={{ required: true }}
          render={({ field, fieldState }) => (
            <SmartField
              data-testid="card-number"
              label={t('checkoutScreen.payment.methods.card.number')}
              type="pan"
              fields={fields}
              onChange={(event) => cardHandler(event, field)}
              fullWidth
              disabled={formState.isSubmitting}
              error={fieldState.invalid || paymentError}
            />
          )}
        />
      </Grid>
      <Grid item xs={6}>
        <Controller
          name="cardExpiration"
          control={control}
          defaultValue={false}
          rules={{ required: true }}
          render={({ field, fieldState }) => (
            <SmartField
              data-testid="card-expiration"
              label={t('checkoutScreen.payment.methods.card.validity')}
              type="expiration"
              fields={fields}
              onChange={(event) => expirationHandler(event, field)}
              fullWidth
              disabled={formState.isSubmitting}
              error={fieldState.invalid || paymentError}
            />
          )}
        />
      </Grid>
      <Grid item xs={6}>
        <Controller
          name="cardCvv"
          control={control}
          defaultValue={false}
          rules={{ required: true }}
          render={({ field, fieldState }) => (
            <SmartField
              data-testid="card-cvv"
              label={t('checkoutScreen.payment.methods.card.cvv')}
              type="cvv"
              fields={fields}
              onChange={(event) => cvvHandler(event, field)}
              fullWidth
              disabled={formState.isSubmitting}
              error={fieldState.invalid || paymentError}
            />
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <PayButton
          fullWidth
          onClick={handleSubmit(pay as SubmitHandler<FieldValues>, onValidationError)}
          disabled={formState.isSubmitting || !checkout.isAllowedToPayWithCard}
          loading={formState.isSubmitting}
          label={t('checkoutScreen.payment.methods.submit')}
        />
      </Grid>
    </Grid>
  );
};

export default observer(DLocalCardForm);
